import {
  Badge,
  DefaultMantineColor,
  getContrastColor,
  Group,
  Stack,
  Text,
  ThemeIcon,
  useMantineTheme,
} from "@mantine/core"

import { PropsWithChildren } from "react"
import { IconSquareRotated, IconSquareRotatedFilled } from "@tabler/icons-react"
import { CalendarAppointment } from "../../../../@client/Theasoft_ApiServer_Calendar"
import { Period } from "../../../../-components/period"
import { CalendarAppointmentPaper } from "./CalendarAppointmentPaper"
import { useTranslation } from "react-i18next"
import { v4 } from "uuid"
import { useRenderCastTime } from "../renderCastTime"

interface Props {
  appointments?: CalendarAppointment[]
}

function AppointmentIcon({
  children,
  color,
}: { color?: DefaultMantineColor } & PropsWithChildren) {
  return (
    <ThemeIcon variant="transparent" color={color}>
      {children}
    </ThemeIcon>
  )
}

function CalendarAppointmentStatusBadge({
  appointment,
}: {
  appointment: CalendarAppointment
}) {
  return appointment.status ? (
    <Badge color={appointment.statusColor ?? "blue"}>
      {appointment.status}
    </Badge>
  ) : null
}

export function AppointmentStack({ appointments }: Props) {
  const { t } = useTranslation("app_server")
  const theme = useMantineTheme()

  const { renderCastTime } = useRenderCastTime()

  return (
    <Stack gap="sm" h="100%" w="100%">
      {appointments?.length ? (
        appointments.map((appointment) => (
          <CalendarAppointmentPaper key={v4()} appointment={appointment}>
            <Stack gap={0}>
              <Group
                wrap="nowrap"
                justify={
                  appointment.start === null && appointment.end === null
                    ? "center"
                    : undefined
                }
              >
                {appointment.start || appointment.end ? (
                  <Period
                    w={85}
                    start={appointment.start}
                    end={appointment.end}
                  />
                ) : null}
                {appointment?.source === "Cast" ||
                appointment?.source === "PossibleCast" ? (
                  <AppointmentIcon
                    color={
                      appointment.foreColorHEX
                        ? appointment.foreColorHEX
                        : appointment.backColorHEX
                          ? getContrastColor({
                              color: appointment.backColorHEX,
                              theme,
                            })
                          : undefined
                    }
                  >
                    {appointment?.source === "PossibleCast" ? (
                      <IconSquareRotated />
                    ) : (
                      <IconSquareRotatedFilled />
                    )}
                  </AppointmentIcon>
                ) : null}
                <Stack gap={0}>
                  <Group>
                    {appointment.type && <Text>{appointment.type}</Text>}
                    <Text
                      style={{
                        ...(appointment.isPublicEvent
                          ? { fontWeight: 500 }
                          : undefined),
                      }}
                    >
                      {appointment.title}
                    </Text>
                    {appointment.location && (
                      <Text>{`(${appointment.location})`}</Text>
                    )}
                    <CalendarAppointmentStatusBadge appointment={appointment} />
                  </Group>
                  {appointment.description && (
                    <Text
                      c={
                        appointment.foreColorHEX === null
                          ? undefined
                          : appointment.foreColorHEX
                      }
                    >
                      {appointment.description}
                    </Text>
                  )}
                </Stack>
              </Group>
              {appointment.additionalCastTimes?.map((t) => (
                <Text>{renderCastTime(t)}</Text>
              ))}
            </Stack>
          </CalendarAppointmentPaper>
        ))
      ) : (
        <Text>{t("NoAppointments")}</Text>
      )}
    </Stack>
  )
}
